import { useState, useEffect } from 'react';
import axios from 'axios';
import { Mail, MapPin, Trash2, Download, Loader2, Rocket, X, Send, CheckCircle2, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WaitlistTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [blast, setBlast] = useState(null); // { zip, promo, percent, dryRun, result }

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/waitlist`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load waitlist');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const removeEntry = async (id) => {
    if (!window.confirm('Remove this entry from the waitlist?')) return;
    const token = localStorage.getItem('token');
    await axios.delete(`${API}/admin/waitlist/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    load();
  };

  const resendConfirmation = async (id) => {
    const token = localStorage.getItem('token');
    try {
      const r = await axios.post(`${API}/admin/waitlist/${id}/resend-confirmation`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.data.sent) {
        alert('Confirmation email re-sent successfully.');
      } else {
        alert(`Send failed: ${r.data.error || 'unknown error'} (check Resend dashboard)`);
      }
      load();
    } catch (e) {
      alert(`Failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const exportCsv = () => {
    if (!data || !data.entries.length) return;
    const rows = [['email', 'zip_code', 'source', 'created_at', 'notified_at']];
    data.entries.forEach(e => {
      rows.push([e.email, e.zip_code || '', e.source || '', e.created_at || '', e.notified_at || '']);
    });
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `waitlist_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const openBlast = (zip) => {
    setBlast({ zip: zip || '', promo: '', percent: 20, personalize: true, dryRun: null, result: null, sending: false });
  };

  const runBlast = async (dryRun) => {
    const token = localStorage.getItem('token');
    setBlast(b => ({ ...b, sending: true, result: null }));
    try {
      const res = await axios.post(
        `${API}/admin/waitlist/launch-blast`,
        {
          zip_code: blast.zip || null,
          promo_code: blast.promo || null,
          promo_percent: blast.percent || 20,
          personalize: blast.personalize,
          dry_run: dryRun,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBlast(b => ({ ...b, sending: false, result: { dryRun, ...res.data } }));
      if (!dryRun) load();
    } catch (e) {
      setBlast(b => ({ ...b, sending: false, result: { error: e.response?.data?.detail || 'Failed' } }));
    }
  };

  if (loading) {
    return (
      <div className="bg-surface rounded-2xl p-6 border border-border flex items-center gap-3" data-testid="waitlist-loading">
        <Loader2 className="w-5 h-5 animate-spin text-primary" />
        <span className="text-sm text-text-secondary">Loading waitlist…</span>
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-surface rounded-2xl p-6 border border-border text-sm text-rose-600" data-testid="waitlist-error">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="waitlist-tab">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-surface rounded-2xl p-5 border border-border">
          <div className="flex items-center gap-2 text-text-secondary text-sm mb-1">
            <Mail className="w-4 h-4" /> Total signups
          </div>
          <p className="font-heading text-3xl font-bold text-primary" data-testid="waitlist-total">{data.total}</p>
        </div>
        <div className="bg-surface rounded-2xl p-5 border border-border md:col-span-2">
          <div className="flex items-center gap-2 text-text-secondary text-sm mb-2">
            <MapPin className="w-4 h-4" /> Top zip codes
          </div>
          {data.top_zips.length === 0 ? (
            <p className="text-sm text-text-secondary">No signups yet.</p>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="waitlist-top-zips">
              {data.top_zips.slice(0, 10).map(z => (
                <button
                  key={z.zip}
                  onClick={() => openBlast(z.zip === '(no zip)' ? '' : z.zip)}
                  className="px-3 py-1 rounded-full bg-primary-light/40 text-primary text-xs font-semibold border border-primary/20 hover:bg-primary hover:text-white transition flex items-center gap-1.5"
                  data-testid={`launch-blast-zip-${z.zip}`}
                  title={`Send launch blast to ${z.count} signups in ${z.zip}`}
                >
                  {z.zip} <span className="opacity-70">· {z.count}</span>
                  <Rocket className="w-3 h-3" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bg-surface rounded-2xl border border-border">
        <div className="p-4 flex items-center justify-between border-b border-border flex-wrap gap-3">
          <h3 className="font-heading text-lg font-semibold text-text-primary">Signups ({data.total})</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => openBlast('')}
              disabled={!data.entries.length}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500 text-white text-sm font-medium hover:bg-amber-600 disabled:opacity-50"
              data-testid="waitlist-launch-all-btn"
            >
              <Rocket className="w-4 h-4" /> Launch blast
            </button>
            {data.sources && data.sources.length > 0 && (
              <div className="flex flex-wrap gap-1 text-xs">
                {data.sources.map(s => (
                  <span key={s.source} className="px-2 py-1 rounded-full bg-surface-muted text-text-secondary">
                    {s.source}: <strong className="text-text-primary">{s.count}</strong>
                  </span>
                ))}
              </div>
            )}
            <button
              onClick={exportCsv}
              disabled={!data.entries.length}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary text-white text-sm font-medium hover:bg-primary-dark disabled:opacity-50"
              data-testid="waitlist-export-csv"
            >
              <Download className="w-4 h-4" /> Export CSV
            </button>
          </div>
        </div>

        {data.entries.length === 0 ? (
          <p className="p-8 text-center text-sm text-text-secondary">No one on the waitlist yet — share your landing page to start collecting signups.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="waitlist-table">
              <thead className="bg-surface-muted text-text-secondary text-left">
                <tr>
                  <th className="px-4 py-2">Email</th>
                  <th className="px-4 py-2">Zip</th>
                  <th className="px-4 py-2">Source</th>
                  <th className="px-4 py-2">Signed up</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {data.entries.map(e => (
                  <tr key={e.id} className="border-t border-border" data-testid="waitlist-row">
                    <td className="px-4 py-2 font-mono text-xs text-text-primary">{e.email}</td>
                    <td className="px-4 py-2 text-text-secondary">{e.zip_code || '—'}</td>
                    <td className="px-4 py-2 text-text-secondary">{e.source || 'unknown'}</td>
                    <td className="px-4 py-2 text-text-secondary text-xs">{new Date(e.created_at).toLocaleString()}</td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-1 flex-wrap">
                        {e.confirmation_sent_at ? (
                          <span className="inline-flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" title={`Sent ${new Date(e.confirmation_sent_at).toLocaleString()}`} data-testid="waitlist-confirmation-sent">
                            <CheckCircle2 className="w-3 h-3" /> Confirmed
                          </span>
                        ) : e.confirmation_error ? (
                          <span className="inline-flex items-center gap-1 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-2 py-0.5" title={e.confirmation_error} data-testid="waitlist-confirmation-failed">
                            <AlertCircle className="w-3 h-3" /> Failed
                          </span>
                        ) : (
                          <span className="text-xs text-text-secondary">— pending</span>
                        )}
                        {e.notified_at && (
                          <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5" data-testid="waitlist-notified-badge">
                            <Rocket className="w-3 h-3" /> Launched
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => resendConfirmation(e.id)}
                          className="text-primary hover:text-primary-dark p-1"
                          title="Resend confirmation email"
                          data-testid="waitlist-resend-btn"
                        >
                          <Send className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => removeEntry(e.id)}
                          className="text-rose-500 hover:text-rose-700 p-1"
                          title="Remove"
                          data-testid="waitlist-delete-btn"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Launch-blast modal */}
      {blast && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => !blast.sending && setBlast(null)}
          data-testid="blast-modal-backdrop"
        >
          <div
            className="bg-surface rounded-2xl shadow-2xl max-w-lg w-full"
            onClick={(e) => e.stopPropagation()}
            data-testid="blast-modal"
          >
            <div className="p-5 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-amber-50">
                  <Rocket className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-text-primary">Send launch blast</h3>
                  <p className="text-xs text-text-secondary">Only un-notified entries get emailed. Idempotent &mdash; safe to re-run.</p>
                </div>
              </div>
              <button onClick={() => !blast.sending && setBlast(null)} className="text-text-secondary hover:text-text-primary" data-testid="blast-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Zip code (blank = all zips)</label>
                <input
                  type="text"
                  placeholder="e.g. 10001"
                  value={blast.zip}
                  onChange={(e) => setBlast({ ...blast, zip: e.target.value })}
                  className="input-field w-full"
                  data-testid="blast-zip-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">Promo code (optional)</label>
                  <input
                    type="text"
                    placeholder="EARLY20"
                    value={blast.promo}
                    onChange={(e) => setBlast({ ...blast, promo: e.target.value.toUpperCase() })}
                    className="input-field w-full font-mono"
                    data-testid="blast-promo-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">% off</label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={blast.percent}
                    onChange={(e) => setBlast({ ...blast, percent: parseInt(e.target.value) || 20 })}
                    className="input-field w-full"
                    data-testid="blast-percent-input"
                  />
                </div>
              </div>

              <label className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-3 cursor-pointer" data-testid="blast-personalize-toggle">
                <input
                  type="checkbox"
                  checked={blast.personalize ?? true}
                  onChange={(e) => setBlast({ ...blast, personalize: e.target.checked })}
                  className="mt-1"
                />
                <div>
                  <p className="text-sm font-semibold text-emerald-900">Personalize with a local cleaner</p>
                  <p className="text-xs text-emerald-800 mt-0.5">
                    Include a top-rated cleaner&apos;s photo, rating, and QR-code booking link in every email &mdash; way higher click-through than a generic promo. Only works when a zip is specified.
                  </p>
                </div>
              </label>

              {blast.result && (
                <div className={`rounded-lg p-3 text-sm ${blast.result.error ? 'bg-rose-50 text-rose-800 border border-rose-200' : 'bg-emerald-50 text-emerald-900 border border-emerald-200'}`} data-testid="blast-result">
                  {blast.result.error ? blast.result.error : (
                    blast.result.dryRun
                      ? <>Preview: <strong>{blast.result.would_send}</strong> emails would be sent. Sample: <span className="font-mono text-xs">{(blast.result.sample || []).join(', ')}</span></>
                      : (
                        <>
                          Sent <strong>{blast.result.sent}</strong> of {blast.result.total_targeted}
                          {blast.result.failed > 0 ? ` (${blast.result.failed} failed)` : ''}.
                          {blast.result.featured_cleaner && (
                            <> Featured cleaner: <strong>{blast.result.featured_cleaner.name}</strong> ({blast.result.featured_cleaner.ref_code}).</>
                          )}
                        </>
                      )
                  )}
                </div>
              )}

              <div className="flex gap-2">
                <button
                  onClick={() => runBlast(true)}
                  disabled={blast.sending}
                  className="flex-1 py-2 px-4 rounded-full border border-border text-text-primary hover:bg-surface-muted text-sm font-medium disabled:opacity-50"
                  data-testid="blast-preview-btn"
                >
                  {blast.sending ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Preview count'}
                </button>
                <button
                  onClick={() => runBlast(false)}
                  disabled={blast.sending}
                  className="flex-1 py-2 px-4 rounded-full bg-amber-500 text-white hover:bg-amber-600 text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="blast-send-btn"
                >
                  {blast.sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
                  Send now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WaitlistTab;
